// Карточка «API-ключи» в настройках учётной записи (IL-ADR-1, план IL-1 1.5).
//
// Три пользовательских действия — выпуск, список, отзыв — поверх ручек
// /api/users/me/api-keys. Секрет ключа существует в интерфейсе ровно один раз:
// в блоке после выпуска, пока человек его не закрыл; в списке живут только
// префикс и метаданные (§I3 — сервер ничем другим и не располагает).
//
// Модуль не импортирует auth.ts (composition root, ADR-002 phase C) — образец
// telegram-link.ts. Признак «человек вошёл» — state.authToken/state.currentUser;
// Free-сессии сервер отвечает 403 registration_required, карточка прячется.

import { state } from './shared/state';
import { $, showToast } from './shared/index';

/**
 * Расход по ключу (IL-5 5.2). `charged_usd` — то, что реально списано с
 * баланса владельца (с наценкой услуги и внешнего канала); `cost_usd` —
 * себестоимость вызовов, она в счёт не входит и в карточке не показывается,
 * чтобы в интерфейсе не соседствовали два числа, из которых платится одно.
 */
export interface ApiKeySpendView {
  requests: number;
  cost_usd: number;
  charged_usd: number;
  last_request_at: number | null;
}

export interface ApiKeyView {
  id: number;
  name: string;
  key_prefix: string;
  scopes: string[];
  rate_limit_rpm: number | null;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
  active: boolean;
  /** Может отсутствовать: старый ответ сервера или незарегистрированный порт. */
  spend?: ApiKeySpendView;
}

interface ApiKeysListResponse {
  keys?: ApiKeyView[];
  max_active?: number;
}

/** Человеческие имена скоупов — там, где ключи показываются списком. */
export const API_KEY_SCOPE_LABELS: Record<string, string> = {
  'council:read': 'Совет: чтение',
  'council:write': 'Совет: создание',
  'conversation:write': 'Разговор: генерация',
  'webhooks:manage': 'Webhook-подписки',
};

function authHeaders(): Record<string, string> {
  return { Authorization: 'Bearer ' + state.authToken };
}

/** Текст ошибки из трёх форм ответа: errorHandler, zod-ветка ручки, admin. */
async function errorMessage(r: Response, fallback: string): Promise<string> {
  try {
    const body = await r.json() as {
      error?: { message?: string } | string;
      detail?: string;
    };
    if (typeof body.error === 'string') return body.error;
    if (body.error?.message) return body.error.message;
    if (body.detail) return body.detail;
  } catch {
    // Тело не JSON — остаёмся с запасным текстом.
  }
  return fallback;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('ru-RU');
}

/**
 * Деньги в карточке — с четырьмя знаками: внешний вызов стоит центы, и
 * округление до копейки показывало бы «$0.00» у ключа, которым пользовались.
 */
function formatSpend(spend: ApiKeySpendView | undefined): string {
  if (!spend || spend.requests <= 0) return '';
  const n = spend.requests;
  const mod10 = n % 10;
  const mod100 = n % 100;
  const word = mod10 === 1 && mod100 !== 11
    ? 'вызов'
    : (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'вызова' : 'вызовов');
  return `${n} ${word} на $${spend.charged_usd.toFixed(4)}`;
}

export function formatApiKeyMeta(key: ApiKeyView): string {
  const scopes = key.scopes.map((s) => API_KEY_SCOPE_LABELS[s] || s).join(', ');
  const parts = [`avg_${key.key_prefix}_…`, scopes];
  const spend = formatSpend(key.spend);
  if (spend) parts.push(spend);
  parts.push(key.last_used_at
    ? `последний вызов ${formatDate(key.last_used_at)}`
    : 'не использовался');
  if (key.revoked_at) parts.push(`отозван ${formatDate(key.revoked_at)}`);
  else if (key.expires_at) parts.push(`до ${formatDate(key.expires_at)}`);
  else parts.push('бессрочный');
  return parts.filter(Boolean).join(' · ');
}

/**
 * Строка списка собирается узлами, а не HTML-строкой: имя ключа — произвольный
 * пользовательский текст, разметкой ему становиться нельзя.
 */
export function buildApiKeyRow(key: ApiKeyView, onRevoke: (key: ApiKeyView) => void): HTMLElement {
  const row = document.createElement('div');
  row.className = 'user-doc-row';

  const info = document.createElement('div');
  info.className = 'user-doc-info';

  const name = document.createElement('span');
  name.className = 'user-doc-name';
  name.textContent = key.name;

  const status = document.createElement('span');
  if (key.active) {
    status.className = 'user-doc-status--ready';
    status.textContent = ' — действует';
  } else {
    status.className = 'user-doc-status--failed';
    status.textContent = key.revoked_at ? ' — отозван' : ' — истёк';
  }
  name.appendChild(status);

  const meta = document.createElement('span');
  meta.className = 'user-doc-meta';
  meta.textContent = formatApiKeyMeta(key);

  info.appendChild(name);
  info.appendChild(meta);
  row.appendChild(info);

  if (key.active) {
    const revoke = document.createElement('button');
    revoke.type = 'button';
    revoke.className = 'nav-btn btn-action-sm--danger';
    revoke.textContent = 'Отозвать';
    revoke.setAttribute('aria-label', `Отозвать ключ ${key.name}`);
    revoke.addEventListener('click', () => onRevoke(key));
    row.appendChild(revoke);
  }

  return row;
}

function renderKeys(keys: ApiKeyView[], maxActive: number | undefined): void {
  const list = $('api-keys-list');
  if (!list) return;
  list.textContent = '';

  if (keys.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'user-docs-empty';
    empty.textContent = 'Ключей пока нет.';
    list.appendChild(empty);
  } else {
    for (const key of keys) list.appendChild(buildApiKeyRow(key, (k) => { void revokeKey(k); }));
  }

  const metaEl = $('api-keys-meta');
  if (metaEl) {
    const active = keys.filter((k) => k.active).length;
    metaEl.textContent = maxActive ? `${active} из ${maxActive}` : '';
  }
}

async function revokeKey(key: ApiKeyView): Promise<void> {
  if (!state.authToken) return;
  if (!window.confirm(
    `Отозвать ключ «${key.name}» (avg_${key.key_prefix}_…)?\n\n`
    + 'Программы, использующие его, сразу перестанут работать. Отзыв необратим.'
  )) return;
  try {
    const r = await fetch(`/api/users/me/api-keys/${key.id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (r.ok) {
      showToast('Ключ отозван');
      void refreshApiKeysCard();
    } else {
      showToast(await errorMessage(r, 'Не удалось отозвать ключ'), 'error');
    }
  } catch {
    showToast('Ошибка сети — ключ не отозван', 'error');
  }
}

// --- Выпуск ----------------------------------------------------------------

function selectedScopes(): string[] {
  return Array.from(document.querySelectorAll<HTMLInputElement>('.api-key-scope-chk:checked'))
    .map((chk) => chk.value);
}

function resetCreateForm(): void {
  const nameEl = $<HTMLInputElement>('api-key-name');
  if (nameEl) nameEl.value = '';
  document.querySelectorAll<HTMLInputElement>('.api-key-scope-chk')
    .forEach((chk) => { chk.checked = false; });
  const expiryEl = $<HTMLSelectElement>('api-key-expiry');
  if (expiryEl) expiryEl.value = '365';
}

function toggleCreateForm(show: boolean): void {
  $('api-keys-create-form')?.classList.toggle('hidden', !show);
  $('api-keys-create-btn')?.classList.toggle('hidden', show);
}

function hideSecret(): void {
  const holder = $('api-keys-secret');
  if (!holder) return;
  holder.classList.add('hidden');
  const value = $('api-keys-secret-value');
  if (value) value.textContent = '';
}

function showSecret(secret: string): void {
  const holder = $('api-keys-secret');
  const value = $('api-keys-secret-value');
  if (!holder || !value) return;
  value.textContent = secret;
  holder.classList.remove('hidden');
}

async function copySecret(): Promise<void> {
  const secret = $('api-keys-secret-value')?.textContent || '';
  if (!secret) return;
  try {
    await navigator.clipboard.writeText(secret);
    showToast('Секрет скопирован');
  } catch {
    showToast('Не удалось скопировать — выделите и скопируйте вручную', 'error');
  }
}

async function issueKey(): Promise<void> {
  if (!state.authToken) return;
  const name = $<HTMLInputElement>('api-key-name')?.value.trim() || '';
  if (!name) {
    showToast('Укажите название ключа', 'error');
    return;
  }
  const scopes = selectedScopes();
  if (scopes.length === 0) {
    showToast('Отметьте хотя бы одно право ключа', 'error');
    return;
  }
  // «365 дней» и прочие сроки — предложение интерфейса; правило сервера — лишь
  // «ISO-строка либо null» (бессрочный ключ допустим §I3 явно).
  const days = Number($<HTMLSelectElement>('api-key-expiry')?.value || '');
  const expiresAt = Number.isFinite(days) && days > 0
    ? new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()
    : null;

  const issueBtn = $<HTMLButtonElement>('api-key-issue-btn');
  if (issueBtn) issueBtn.disabled = true;
  try {
    const r = await fetch('/api/users/me/api-keys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ name, scopes, expires_at: expiresAt }),
    });
    if (r.status === 201) {
      const d = await r.json() as { secret?: string };
      toggleCreateForm(false);
      resetCreateForm();
      if (d.secret) showSecret(d.secret);
      showToast('Ключ выпущен — скопируйте секрет, ещё раз он не покажется');
      void refreshApiKeysCard();
    } else {
      showToast(await errorMessage(r, 'Не удалось выпустить ключ'), 'error');
    }
  } catch {
    showToast('Ошибка сети — ключ не выпущен', 'error');
  } finally {
    if (issueBtn) issueBtn.disabled = false;
  }
}

// --- Карточка --------------------------------------------------------------

/**
 * Актуализация карточки — вместе с формой настроек (и при входе, и при каждом
 * открытии). Гостям и Free карточка не показывается: сервер отвечает 403
 * registration_required, ключу нужен плательщик (ADR-003).
 */
export async function refreshApiKeysCard(): Promise<void> {
  const card = $('api-keys-card');
  if (!card) return;
  if (!state.authToken || !state.currentUser) {
    card.classList.add('hidden');
    hideSecret();
    return;
  }
  try {
    const r = await fetch('/api/users/me/api-keys', { headers: authHeaders() });
    if (!r.ok) {
      card.classList.add('hidden');
      return;
    }
    const d = await r.json() as ApiKeysListResponse;
    renderKeys(d.keys || [], d.max_active);
    card.classList.remove('hidden');
  } catch {
    card.classList.add('hidden');
  }
}

export function initApiKeys(): void {
  $('api-keys-create-btn')?.addEventListener('click', () => {
    hideSecret();
    toggleCreateForm(true);
    $<HTMLInputElement>('api-key-name')?.focus();
  });
  $('api-key-cancel-btn')?.addEventListener('click', () => {
    toggleCreateForm(false);
    resetCreateForm();
  });
  $('api-key-issue-btn')?.addEventListener('click', () => { void issueKey(); });
  $('api-keys-secret-copy')?.addEventListener('click', () => { void copySecret(); });
  $('api-keys-secret-close')?.addEventListener('click', hideSecret);
}
